import React, { useEffect } from "react";
import PublicHeader from "../components/PublicHeader";
import PublicFooter from "../components/PublicFooter";
import { motion } from "framer-motion";
import { 
  Server as ServerIcon, 
  Search as DocumentMagnifyingGlassIcon, 
  Cpu as CpuChipIcon, 
  ShieldCheck as ShieldCheckIcon 
} from "lucide-react";

export default function DigitalAssistantPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col font-sans overflow-hidden pt-20">
      <PublicHeader isHomePage={false} />
      
      {/* HERO SECTION */}
      <section className="relative bg-[#145886] overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10"></div>
        <div className="max-w-7xl mx-auto px-6 py-20 lg:py-32 relative z-10 flex flex-col lg:flex-row items-center">
          <div className="w-full lg:w-1/2 text-center lg:text-left">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white leading-tight mb-3">
                CA Digital <span className="text-[#F37920]">Assistant</span>
              </h1>
              <p className="text-lg md:text-xl text-white/80 mb-3 max-w-2xl mx-auto lg:mx-0">
                The ultimate desktop software for tax professionals. Leverage private AI models, automated ITAT case law scraping, and real-time CBDT statute updates directly on your local machine.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <a href="#download" className="bg-[#F37920] text-white px-8 py-3 rounded-full font-bold hover:bg-[#e06a19] transition shadow-lg text-lg text-center">
                  Download Free Trial
                </a>
                <a href="#features" className="bg-white/10 text-white border border-white/20 px-8 py-3 rounded-full font-bold hover:bg-white/20 transition text-lg text-center">
                  Explore Features
                </a>
              </div>
            </motion.div>
          </div>
          
          <div className="w-full lg:w-1/2 mt-16 lg:mt-0 flex justify-center">
            <motion.div 
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative w-full max-w-md"
            >
              <div className="absolute -inset-1 bg-gradient-to-r from-[#F37920] to-[#145886] rounded-xl blur opacity-30"></div>
              <div className="bg-slate-900 rounded-xl border border-slate-700 shadow-2xl overflow-hidden relative">
                <div className="bg-slate-800 px-4 py-2 flex items-center gap-2 border-b border-slate-700">
                  <div className="w-3 h-3 rounded-full bg-red-500"></div>
                  <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                  <div className="w-3 h-3 rounded-full bg-green-500"></div>
                  <div className="text-slate-400 text-xs ml-4 font-mono">CA Digital Assistant v2.1</div>
                </div>
                <div className="p-6 font-mono text-sm text-green-400">
                  <p className="mb-2">~ CADA: Syncing Knowledge Base...</p>
                  <p className="mb-2">~ CADA: 432 ITAT Orders vectorized.</p>
                  <p className="mb-2">~ CADA: CBDT Notifications downloaded.</p>
                  <p className="mb-2">~ CADA: Loading AI Model (Llama-3-8B-Instruct)...</p>
                  <p className="mb-2 text-white/80">System Ready.</p>
                  <div className="mt-4 flex">
                    <span className="text-white">&gt; </span>
                    <span className="ml-2 border-r-4 border-green-400 animate-pulse w-2 h-4"></span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FEATURES SECTION */}
      <section id="features" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-extrabold text-[#145886] mb-4">Powerful Features for Tax Professionals</h2>
            <p className="text-lg text-slate-500 max-w-2xl mx-auto">Everything you need to stay compliant and draft faster, running securely on your own hardware.</p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-slate-50 rounded-2xl p-6 border border-slate-100 hover:shadow-lg transition">
              <div className="w-12 h-12 bg-blue-100 text-[#145886] rounded-lg flex items-center justify-center mb-3">
                <CpuChipIcon className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-bold text-slate-800 mb-3">Local AI Models</h3>
              <p className="text-slate-600">Draft ITAT appeals and tax memos securely. We push the latest GGUF models (Fast & Draft modes) directly to your machine.</p>
            </div>
            
            <div className="bg-slate-50 rounded-2xl p-6 border border-slate-100 hover:shadow-lg transition">
              <div className="w-12 h-12 bg-orange-100 text-[#F37920] rounded-lg flex items-center justify-center mb-3">
                <DocumentMagnifyingGlassIcon className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-bold text-slate-800 mb-3">ITAT Order Vectorization</h3>
              <p className="text-slate-600">Our scraper continuously aggregates ITAT orders. Search semantically to find relevant case laws instantly.</p>
            </div>
            
            <div className="bg-slate-50 rounded-2xl p-6 border border-slate-100 hover:shadow-lg transition">
              <div className="w-12 h-12 bg-blue-100 text-[#145886] rounded-lg flex items-center justify-center mb-3">
                <ServerIcon className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-bold text-slate-800 mb-3">CBDT Updates</h3>
              <p className="text-slate-600">Never miss a Notification or Circular. Your local database syncs with our master server to pull the latest direct tax statutes.</p>
            </div>
            
            <div className="bg-slate-50 rounded-2xl p-6 border border-slate-100 hover:shadow-lg transition">
              <div className="w-12 h-12 bg-orange-100 text-[#F37920] rounded-lg flex items-center justify-center mb-3">
                <ShieldCheckIcon className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-bold text-slate-800 mb-3">Secure Licensing</h3>
              <p className="text-slate-600">Hardware-locked license keys via our cryptographic License Server to ensure your software is authentic and secure.</p>
            </div>
          </div>
        </div>
      </section>

      {/* PRICING SECTION */}
      <section id="download" className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-extrabold text-[#145886] mb-4">Get Started Today</h2>
            <p className="text-lg text-slate-500 max-w-2xl mx-auto">Download the desktop application for Windows or macOS.</p>
          </div>
          
          <div className="max-w-4xl mx-auto bg-white rounded-3xl shadow-xl overflow-hidden flex flex-col md:flex-row">
            <div className="w-full md:w-1/2 p-8 lg:p-12 border-b md:border-b-0 md:border-r border-slate-100">
              <div className="inline-block px-3 py-1 bg-green-100 text-green-700 text-sm font-bold rounded-full mb-3">TRIAL</div>
              <h3 className="text-2xl font-bold text-slate-800 mb-2">Free Trial</h3>
              <p className="text-slate-500 mb-3">Test drive the Digital Assistant on your local machine for 3 days.</p>
              
              <ul className="space-y-4 mb-3">
                <li className="flex items-center text-slate-600"><span className="text-green-500 mr-2">✓</span> Full ITAT Database Access</li>
                <li className="flex items-center text-slate-600"><span className="text-green-500 mr-2">✓</span> Basic AI Drafting Model</li>
                <li className="flex items-center text-slate-600"><span className="text-green-500 mr-2">✓</span> Hardware-locked Trial Key</li>
              </ul>
              
              <button className="w-full py-3 bg-slate-100 text-slate-700 rounded-lg font-bold hover:bg-slate-200 transition">
                Download Trial
              </button>
            </div>
            
            <div className="w-full md:w-1/2 p-8 lg:p-12 bg-[#145886]">
              <div className="inline-block px-3 py-1 bg-[#F37920] text-white text-sm font-bold rounded-full mb-3">PRO</div>
              <h3 className="text-2xl font-bold text-white mb-2">Professional Subscription</h3>
              <p className="text-white/80 mb-3">Full access to advanced models and daily syncs.</p>
              
              <ul className="space-y-4 mb-3">
                <li className="flex items-center text-white/90"><span className="text-[#F37920] mr-2">✓</span> Advanced LLM Models (Fast & Draft)</li>
                <li className="flex items-center text-white/90"><span className="text-[#F37920] mr-2">✓</span> Real-time CBDT Statutory Updates</li>
                <li className="flex items-center text-white/90"><span className="text-[#F37920] mr-2">✓</span> Vectorized Knowledge Base Sync</li>
                <li className="flex items-center text-white/90"><span className="text-[#F37920] mr-2">✓</span> Cryptographic Seat Licensing</li>
              </ul>
              
              <a href="/login" className="block w-full py-3 bg-[#F37920] text-white text-center rounded-lg font-bold hover:bg-[#e06a19] transition">
                Login to Purchase Key
              </a>
            </div>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
